import {
  createRouter,
  createWebHistory,
} from 'vue-router';
import type { Pinia } from 'pinia';
import { isLogin, clearToken, gotoLogin } from '@/utils/auth';
import useUserStore from '../stores/modules/user';
import { useProfile } from '@/composables/use-profile';
import { routes } from './routes';

/** 共享判定：仅判断，不产生副作用，两端共用 */
export function needAuth(to: { matched: { meta: Record<string, any> }[] }) {
  return to.matched.some(record => record.meta.requiresAuth);
}

export function createAppRouter({ pinia }: { pinia: Pinia }) {
  const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }

      // 带 hash（如 /#exams）时定位到对应锚点，并为固定页头留出偏移
      if (to.hash) {
        return { el: to.hash, top: 80 };
      }

      return { top: 0 };
    },
  });

  router.beforeEach(async (to) => {
    const userStore = useUserStore(pinia);

    if (isLogin()) {
      if (!userStore.user.id) {
        const { isSuccess } = await userStore.fetchInfo();

        if (!isSuccess) {
          // 登录有效但是取不出来用户信息，说明登录信息有问题，属于未登录状态
          // 清理登录信息
          clearToken();
        }
      }

      // 登录后首次导航加载一次个人信息，使首页等非用户中心页面也能拿到档案状态
      const { ensureProfileLoaded } = useProfile();
      await ensureProfileLoaded();
    }
    else if (needAuth(to)) {
      // 未登录访问需鉴权页面：触发 OAuth 登录，登录后回到目标页
      gotoLogin(to.fullPath);
      return false;
    }

    return true;
  });

  import('./client-progress').then(m => m.installProgress(router));

  return router;
}
